import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid,
  Paper,
  Divider,
} from "@mui/material";
import { updateResults } from "../../../services/PatientlabTestService";
import { recordSampleResultEntered } from "../../../services/sampleTransactionService";
import { assertPathBomStockForResultEntry } from "../../../services/pathBomInventoryGate";
import PathBomInsufficientStockDialog from "../../../components/PathBomInsufficientStockDialog";
import {
  buildCompactResultPayload,
  buildIpdResultUpdatePayload,
  filterSubCategoryResultsForTest,
  getIpdLabTestStatus,
  getSubCategoriesForTest,
  hasSubCategories,
  isIpdResultEditable,
  parseResultForEntryForm,
} from "../labTestWorkflowUtils";

const buildInitialFormState = (test) => {
  const testName = test.resolvedTestName || test.testName;
  const subCategories = hasSubCategories(testName) ? getSubCategoriesForTest(testName) : [];
  const existingResult = test?.testResult || test?.result || "";
  const parsed = parseResultForEntryForm(existingResult);

  if (subCategories.length > 0) {
    const subCategoryResults = {};
    subCategories.forEach((sub) => {
      subCategoryResults[sub.name] = "";
    });
    Object.assign(
      subCategoryResults,
      filterSubCategoryResultsForTest(testName, parsed.subCategoryResults || {})
    );
    return {
      result: "",
      remarks: parsed.remarks || "",
      subCategoryResults,
    };
  }

  return {
    result: parsed.result || "",
    remarks: parsed.remarks || "",
    subCategoryResults: {},
  };
};

const IpdTestResultFields = ({ test, formData, onChange }) => {
  const testName = test.resolvedTestName || test.testName;
  const subCategories = hasSubCategories(testName) ? getSubCategoriesForTest(testName) : [];
  const { result, remarks, subCategoryResults } = formData;

  const handleSubCategoryChange = (name, value) => {
    onChange({
      ...formData,
      subCategoryResults: { ...subCategoryResults, [name]: value },
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {testName}
      </Typography>

      {subCategories.length > 0 ? (
        <Box>
          {subCategories.map((subCategory) => (
            <Grid container spacing={1.5} key={subCategory.name} alignItems="center" sx={{ mb: 1 }}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" fontWeight="bold">
                  {subCategory.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ref: {subCategory.referenceValue} ({subCategory.unit})
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Result"
                  value={subCategoryResults[subCategory.name] || ""}
                  onChange={(e) => handleSubCategoryChange(subCategory.name, e.target.value)}
                />
              </Grid>
            </Grid>
          ))}
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Remarks"
            value={remarks}
            onChange={(e) => onChange({ ...formData, remarks: e.target.value })}
            sx={{ mt: 1 }}
          />
        </Box>
      ) : (
        <TextField
          fullWidth
          label="Test Result"
          value={result}
          onChange={(e) => onChange({ ...formData, result: e.target.value })}
        />
      )}
    </Paper>
  );
};

const validateFormData = (test, formData) => {
  const testName = test.resolvedTestName || test.testName;
  const subCategories = hasSubCategories(testName) ? getSubCategoriesForTest(testName) : [];

  if (subCategories.length) {
    const hasAnyValue = Object.values(formData.subCategoryResults || {}).some(
      (value) => value && value.trim() !== ""
    );
    if (!hasAnyValue) {
      return `Please enter at least one result for ${testName}.`;
    }
    return null;
  }

  if (!formData.result?.trim()) {
    return `Please enter a result for ${testName}.`;
  }
  return null;
};

const buildFinalResult = (test, formData) => {
  const testName = test.resolvedTestName || test.testName;
  const subCategories = hasSubCategories(testName) ? getSubCategoriesForTest(testName) : [];
  if (subCategories.length) {
    return buildCompactResultPayload(formData.subCategoryResults, formData.remarks, testName);
  }
  return formData.result.trim();
};

const IpdTestResultsPanel = ({ group, onSubmitted, embedded = false, onSubmittingChange, focusTestId }) => {
  const [formState, setFormState] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockChecks, setStockChecks] = useState([]);

  const editableTests = useMemo(() => {
    const tests = (group?.labTests || []).filter(isIpdResultEditable);
    if (!focusTestId) return tests;
    return tests.filter((test) => Number(test.id) === Number(focusTestId));
  }, [group?.labTests, focusTestId]);

  useEffect(() => {
    const nextState = {};
    editableTests.forEach((test) => {
      nextState[test.id] = buildInitialFormState(test);
    });
    setFormState(nextState);
  }, [editableTests]);

  const handleSubmitAll = async () => {
    if (submitting || editableTests.length === 0) return;

    for (const test of editableTests) {
      const error = validateFormData(test, formState[test.id] || {});
      if (error) {
        alert(error);
        return;
      }
      if (!test?.id) {
        alert(`Cannot save results: missing test id for ${test.resolvedTestName || test.testName || "unknown test"}.`);
        return;
      }
      if (test.assignedBy == null) {
        alert(`Cannot save results: doctor assignment is missing for ${test.resolvedTestName || test.testName || "unknown test"}.`);
        return;
      }
    }

    setSubmitting(true);
    onSubmittingChange && onSubmittingChange(true);

    try {
      const testsNeedingStockGate = editableTests.filter(
        (test) => !["COMPLETED", "COMPLETE"].includes(getIpdLabTestStatus(test))
      );
      if (testsNeedingStockGate.length > 0) {
        const alreadyCollected = testsNeedingStockGate.every((test) =>
          ["COLLECTED"].includes(getIpdLabTestStatus(test))
        );
        const stockGate = await assertPathBomStockForResultEntry({
          patientLabTests: testsNeedingStockGate.map((test) => ({
            ...test,
            labTestId: test.testId || test.labTestId,
            testId: test.testId || test.labTestId,
            testName: test.resolvedTestName || test.testName,
            resolvedTestName: test.resolvedTestName || test.testName,
          })),
          alreadyCollected,
        });
        if (!stockGate.ok) {
          setStockChecks(stockGate.checks || []);
          setStockDialogOpen(true);
          return;
        }
      }

      const payloads = editableTests.map((test) =>
        buildIpdResultUpdatePayload(test, buildFinalResult(test, formState[test.id]))
      );

      await updateResults(payloads);

      for (const test of editableTests) {
        await recordSampleResultEntered({
          prescriptionId:
            test.prescriptionId ??
            test.ipdPrescriptionId ??
            test.ipd_prescription_id ??
            group?.prescriptionId,
          patientId: test.patientId ?? test.patient_id ?? group?.patientId,
          patientName: test.patientName ?? group?.patientName,
          labTestId: test.testId || test.labTestId,
          labTestName: test.resolvedTestName || test.testName,
          billed: Boolean(test.invoiceId || test.invoice_id || group?.invoiceId),
          invoiceId: test.invoiceId ?? test.invoice_id ?? group?.invoiceId,
          admissionId: test.admissionId ?? group?.admissionId,
          reportId: test.reportId ?? test.report_id ?? test.id,
          patientType: "ipd",
          sampleCollected: true,
          result: true,
        });
      }

      // Pathology inventory is deducted at sample collection (not on result save).

      const editingExisting = editableTests.every((test) =>
        ["COMPLETED", "COMPLETE"].includes(getIpdLabTestStatus(test))
      );
      alert(
        editingExisting && editableTests.length <= 1
          ? "Result updated successfully."
          : !editingExisting && editableTests.length <= 1
            ? "Result saved successfully."
            : editingExisting
              ? "Results updated successfully."
              : "All test results saved successfully."
      );
      onSubmitted && (await onSubmitted());
    } catch (error) {
      console.error("[IpdTestResultsPanel] Failed to save results:", error);
      alert("Failed to save test results. Please try again.");
    } finally {
      setSubmitting(false);
      onSubmittingChange && onSubmittingChange(false);
    }
  };

  if (editableTests.length === 0) return null;

  const isEditMode = editableTests.every((test) =>
    ["COMPLETED", "COMPLETE"].includes(getIpdLabTestStatus(test))
  );
  const isSingleTest = editableTests.length <= 1;
  const helperText = isEditMode
    ? isSingleTest
      ? "Update the result below, then submit."
      : "Update the result(s) below, then submit."
    : isSingleTest
      ? "Enter the result below, then submit."
      : "Enter results for all collected tests below, then submit once.";
  const submitLabel = isEditMode
    ? isSingleTest
      ? "Update Result"
      : "Update Results"
    : isSingleTest
      ? "Submit Result"
      : "Submit All Results";

  const content = (
    <>
      {!embedded && (
        <>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {isEditMode ? "Edit Results" : "Add Results"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {helperText}
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </>
      )}
      {embedded && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {helperText}
        </Typography>
      )}
      <Box sx={{ maxHeight: embedded ? 480 : 420, overflowY: "auto", pr: 1 }}>
        {editableTests.map((test) => (
          <IpdTestResultFields
            key={test.id}
            test={test}
            formData={formState[test.id] || { result: "", remarks: "", subCategoryResults: {} }}
            onChange={(next) => setFormState((prev) => ({ ...prev, [test.id]: next }))}
          />
        ))}
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <Box display="flex" justifyContent="flex-end">
        <Button variant="contained" color="primary" onClick={handleSubmitAll} disabled={submitting}>
          {submitting ? "Processing..." : submitLabel}
        </Button>
      </Box>
    </>
  );

  const stockDialog = (
    <PathBomInsufficientStockDialog
      open={stockDialogOpen}
      onClose={() => setStockDialogOpen(false)}
      checks={stockChecks}
      actionLabel="add / update result"
      title="Cannot save result — consumable not in inventory"
    />
  );

  if (embedded) {
    return (
      <>
        {stockDialog}
        {content}
      </>
    );
  }

  return (
    <>
      {stockDialog}
      <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
        {content}
      </Box>
    </>
  );
};

export default IpdTestResultsPanel;
